<template>
  <div class="text-white min-h-screen flex flex-col justify-between">
    <!-- Main Content -->
    <main class="flex-1 flex flex-col items-center justify-center relative pt-40 pb-24">
      <div class="absolute inset-0 w-full h-full bg-cover bg-center z-[-1]" :style="bgStyle"></div>

      <!-- Deposit Text -->
      <div class="text-center text-[#D9D9D9] space-y-4 mb-8">
        <h1 class="text-2xl uppercase font-[DM_Sans]">{{ $t('deposit.title') }}</h1>
        <p class="text-base font-[DM_Sans] leading-relaxed">
          {{ $t('deposit.reservationFeeText') }} 
          <span class="text-[#F5BE32] font-bold">70</span> 
          {{ $t('deposit.currency', { amount: '' }) }}
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="depositPlaced" class="flex flex-col items-center justify-center w-full h-full min-h-[60vh] p-6">
        <!-- Receipt -->
        <div class="receipt-print bg-[#232323] rounded-lg p-8 shadow-lg max-w-2xl w-full">
          <!-- Receipt Header -->
          <div class="text-center mb-6">
            <h2 class="text-2xl font-bold text-[#F5BE32] mb-2">{{ $t("deposit.receipt.title") }}</h2>
            <p class="text-gray-400 text-sm">{{ $t("deposit.receipt.subtitle") }}</p>
            <div class="w-full h-px bg-[#F5BE32] mt-4"></div>
          </div>

          <!-- Receipt Details -->
          <div class="space-y-4 mb-6">
            <div class="flex justify-between">
              <span class="text-gray-400">{{ $t("deposit.receipt.receiptNumber") }}</span>
              <span class="text-white font-semibold">{{ depositNumber }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-400">{{ $t("deposit.receipt.date") }}</span>
              <span class="text-white">{{ depositDate }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-400">{{ $t("deposit.receipt.paymentMethod") }}</span>
              <span class="text-white capitalize">{{ getTranslatedPaymentMethod(paymentMethod) }}</span>
            </div>
          </div>

          <!-- Payment Details -->
          <div class="border-t border-gray-600 pt-4 mb-6">
            <!-- Always show reservation fee -->
            <div class="flex justify-between items-center mb-2">
              <span class="text-gray-400">{{ $t("deposit.payment.reservationFee") }}</span>
              <span class="text-white">{{ $t("deposit.currency", { amount: "70.00" }) }}</span>
            </div>
            
            <!-- Show preorder breakdown if there are items -->
            <div v-if="receiptCartTotal > 0" class="space-y-2 mb-4">
              <div class="flex justify-between items-center">
                <span class="text-gray-400">{{ $t("deposit.payment.preorderTotal") }}</span>
                <span class="text-white">{{ $t("deposit.currency", { amount: receiptCartTotal.toFixed(2) }) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-400">{{ $t("cart.serviceFee") }}</span>
                <span class="text-white">{{ $t("deposit.currency", { amount: receiptServiceFee.toFixed(2) }) }}</span>
              </div>
            </div>
            
            <!-- Total amount -->
            <div class="flex justify-between items-center border-t border-gray-600 pt-2">
              <span class="text-lg text-gray-400">{{ $t("deposit.receipt.totalAmount") }}</span>
              <span class="text-2xl font-bold text-[#F5BE32]">{{ $t("deposit.currency", { amount: receiptTotalAmount.toFixed(2) }) }}</span>
            </div>
          </div>

          <!-- Status -->
          <div class="text-center p-4 bg-green-900 text-green-200 rounded-lg mb-6">
            <p class="font-semibold">{{ $t("deposit.receipt.success") }}</p>
            <p class="text-sm mt-1">{{ $t("deposit.receipt.successDesc") }}</p>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-4 no-print">
            <button @click="printReceipt" class="flex-1 bg-[#F5BE32] text-black font-bold py-3 rounded hover:bg-yellow-400 transition">
              {{ $t("deposit.receipt.printReceipt") }}
            </button>
            <button @click="goToHome" class="flex-1 bg-gray-600 text-white font-bold py-3 rounded hover:bg-gray-700 transition">
              {{ $t("deposit.receipt.goToHome") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Payment Form -->
      <section v-else class="rounded-xl p-10 w-full max-w-xl mx-auto bg-[#1F1F1F] shadow-lg">
        <div class="text-center mb-6">
          <div class="text-sm text-gray-400 mb-2">{{ $t("deposit.payment.totalOrder") }}</div>
          <div class="text-3xl font-bold text-[#F5BE32]">{{ $t("deposit.currency", { amount: depositAmount.toFixed(2) }) }}</div>
          <div v-if="cartTotal > 0" class="text-sm text-gray-400 mt-2">
            <div>{{ $t("deposit.payment.reservationFee") }}: {{ $t("deposit.currency", { amount: "70.00" }) }}</div>
            <div>{{ $t("deposit.payment.preorderTotal") }}: {{ $t("deposit.currency", { amount: cartTotal.toFixed(2) }) }}</div>
            <div>{{ $t("cart.serviceFee") }}: {{ $t("deposit.currency", { amount: serviceFee.toFixed(2) }) }}</div>
          </div>
        </div>

        <!-- Card Logos & Instructions -->
        <div class="flex items-center gap-4 w-full max-w-md mx-auto mb-6">
          <div class="flex flex-col items-center gap-1">
            <span class="text-white text-sm">VISA</span>
            <svg width="24" height="24" viewBox="0 0 24 24">
              <circle cx="8" cy="12" r="6" fill="#F79E1B" />
              <circle cx="16" cy="12" r="6" fill="#EA001B" />
            </svg>
          </div>
          <div class="w-px h-12 bg-[#F5BE32]"></div>
          <div class="flex flex-col">
            <span class="text-xs font-bold text-[#D9D9D9]">{{ $t('deposit.cardTitle') }}</span>
            <span class="text-xs text-[#D9D9D9]">{{ $t('deposit.cardDesc') }}</span>
          </div>
        </div>

        <!-- Payment Method Selection -->
        <div class="mb-6">
          <div class="space-y-2">
            <span class="font-semibold text-lg text-[#D9D9D9]">{{ $t("deposit.payment.paymentMethod") }}</span>
            <div class="w-full h-px bg-[#F5BE32]"></div>
          </div>
          <div class="mt-4 space-y-3">
            <label class="flex items-center cursor-pointer" :class="$i18n.locale === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-3'">
              <input type="radio" v-model="paymentMethod" value="card" class="text-[#F5BE32] bg-[#1A1A1A] border-[#D9D9D9] focus:ring-[#F5BE32]">
              <span class="text-[#D9D9D9]">{{ $t("deposit.payment.creditCard") }}</span>
            </label>
            <label class="flex items-center cursor-pointer" :class="$i18n.locale === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-3'">
              <input type="radio" v-model="paymentMethod" value="cash" class="text-[#F5BE32] bg-[#1A1A1A] border-[#D9D9D9] focus:ring-[#F5BE32]">
              <span class="text-[#D9D9D9]">{{ $t("deposit.payment.cashOnArrival") }}</span>
            </label>
          </div>
        </div>

        <!-- Stripe Card Element -->
        <div v-if="paymentMethod === 'card'" class="mb-6">
          <div class="space-y-4">
            <div class="space-y-2">
              <span class="font-semibold text-lg text-[#D9D9D9]">{{ $t("deposit.payment.paymentInformation") }}</span>
              <div class="w-full h-px bg-[#F5BE32]"></div>
            </div>
            <div v-if="!stripeLoaded" class="text-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5BE32] mx-auto"></div>
              <p class="text-gray-400 mt-2">{{ $t("deposit.payment.loadingPayment") }}</p>
            </div>
            <div v-else>
              <div id="card-element" class="w-full px-4 py-3 rounded bg-[#1A1A1A] border border-[#D9D9D9] text-white min-h-[50px]"></div>
              <div v-if="cardError" class="text-red-400 text-sm mt-2">{{ cardError }}</div>
              <!-- Payment Status -->
              <div v-if="paymentStatus" class="mt-4 p-3 rounded" :class="paymentStatus === 'processing' ? 'bg-yellow-900 text-yellow-200' : paymentStatus === 'success' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'">
                <div v-if="paymentStatus === 'processing'" class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-200 mr-2"></div>
                  {{ $t("deposit.payment.processingPayment") }}
                </div>
                <div v-else-if="paymentStatus === 'success'" class="flex items-center">
                  <span class="mr-2">✅</span>
                  {{ $t("deposit.payment.paymentSuccessful") }}
                </div>
                <div v-else class="flex items-center">
                  <span class="mr-2">❌</span>
                  {{ $t("deposit.payment.paymentFailed") }}
                </div>
              </div>
            </div>
            <!-- Security Note -->
            <div class="flex items-start p-3 border border-[#F3D382] rounded">
              <img src="@/assets/icons/lock.svg" alt="Lock Icon" class="w-6 h-6 self-center" :class="$i18n.locale === 'ar' ? 'ml-3' : 'mr-2'" />
              <p class="text-xs text-gray-300">
                {{ $t("deposit.payment.securityNote") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Cash Payment Note -->
        <div v-if="paymentMethod === 'cash'" class="mb-6">
          <div class="bg-blue-900 text-blue-200 p-4 rounded-lg">
            <p class="text-sm">
              <strong>{{ $t("deposit.payment.cashPayment") }}</strong>
            </p>
          </div>
        </div>

        <button
          :disabled="!paymentMethod || (paymentMethod === 'card' && cardError)"
          @click="processDeposit"
          class="w-full py-3 rounded bg-[#F5BE32] text-black font-semibold flex justify-center items-center disabled:opacity-60 disabled:cursor-not-allowed hover:bg-yellow-400 transition"
        >
          <span v-if="paymentStatus === 'processing'" class="flex items-center">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
            Processing...
          </span>
          <span v-else>
            {{ paymentMethod === 'cash' ? $t("deposit.payment.continueWithCash") : $t("deposit.payment.payDeposit") }}
            <span class="ml-2">&gt;</span>
          </span>
        </button>

        <div class="flex flex-row justify-center mt-4 space-x-2" :class="$i18n.locale === 'ar' ? 'flex-row-reverse' : 'flex-row'">
          <span class="w-2 h-2 rounded-full" :class="$i18n.locale === 'ar' ? 'bg-yellow-500' : 'bg-gray-600'"></span>
          <span class="w-2 h-2 bg-gray-600 rounded-full"></span>
          <span class="w-2 h-2 bg-gray-600 rounded-full"></span>
          <span class="w-2 h-2 rounded-full" :class="$i18n.locale === 'ar' ? 'bg-gray-600' : 'bg-yellow-500'"></span>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { createPaymentIntent } from '@/stripe.js';
import depositBg from "@/assets/Booking/deposit_bg.jpg";
import { getFirestore, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import sendConfirmationEmail from '@/utils/sendConfirmationEmail.js';
import { clearBookingData } from '../utils/clearLocalStorage.js';

export default {
  name: 'DepositPage',
  data() {
    return {
      bgStyle: {
        backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.80), rgba(0,0,0,0.80)), url(${depositBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      },
      paymentMethod: '',
      stripe: null,
      elements: null,
      cardElement: null,
      stripeLoaded: false,
      cardError: null,
      paymentStatus: null,
      clientSecret: null,
      depositPlaced: false,
      depositNumber: '',
      depositDate: '',
      cart: [],
      reservationId: null,
      receiptCartTotal: 0,
      receiptServiceFee: 0,
      receiptTotalAmount: 0
    };
  },
  async mounted() {
    await this.initializeStripe();
    this.loadCart();
    this.loadReservationId();
  },
  beforeUnmount() {
    this.cleanupStripe();
  },
  computed: {
    depositAmount() {
      const reservationFee = 70;
      const cartTotal = this.cartTotal;
      const serviceFee = cartTotal * 0.15; // 15% service fee
      return reservationFee + cartTotal + serviceFee;
    },
    cartTotal() {
      return this.cart.reduce((total, item) => {
        return total + (item.price * (item.quantity || 1));
      }, 0);
    },
    serviceFee() {
      return this.cartTotal * 0.15; // 15% service fee
    }
  },
  methods: {
    loadCart() {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        this.cart = JSON.parse(savedCart);
      }
    },
    async initializeStripe() {
      try {
        this.stripe = await loadStripe('pk_test_51Rp7vnII0qgwIszbRoELwT1n2YdhWtsl3JHFHf1VMepciQadqc7WRFMhBEqVJ3qIlw3cChsZmA5Rqk2SJ7T8bxOz00no6UIUT5');
        this.stripeLoaded = true;
      } catch (error) {
        console.error('Failed to load Stripe:', error);
      }
    },
    setupStripeElements() {
      if (!this.stripe) return;

      // Clean up existing card element if it exists
      if (this.cardElement) {
        this.cardElement.destroy();
        this.cardElement = null;
      }

      this.elements = this.stripe.elements();
      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            color: '#D9D9D9',
            fontSize: '16px',
            '::placeholder': {
              color: '#666',
            },
          },
        },
      });

      this.$nextTick(() => {
        const cardElementContainer = document.getElementById('card-element');
        if (cardElementContainer) {
          this.cardElement.mount('#card-element');
          this.cardElement.on('change', (event) => {
            this.cardError = event.error ? event.error.message : null;
          });
        }
      });
    },
    cleanupStripe() {
      if (this.cardElement) {
        this.cardElement.destroy();
        this.cardElement = null;
      }
    },
    async processDeposit() {
      if (!this.reservationId) {
        console.error('No reservation ID found, redirecting to bookingduration');
        this.$router.push('/bookingduration');
        return;
      }

      if (this.paymentMethod === 'cash') {
        // Handle cash payment
        this.depositPlaced = true;
        this.depositNumber = this.generateDepositNumber();
        this.depositDate = new Date().toLocaleDateString() + ' ' + this.$t('deposit.receipt.dateAt') + ' ' + new Date().toLocaleTimeString();
        this.saveDepositToHistory();
        await this.updateReservationStatus('confirmed');
        this.scrollToReceipt();
        return;
      }

      if (this.paymentMethod === 'card') {
        await this.processStripePayment();
      }
    },
    async processStripePayment() {
      if (!this.reservationId) {
        console.error('No reservation ID found, redirecting to bookingduration');
        this.$router.push('/bookingduration');
        return;
      }

      if (!this.stripe || !this.cardElement) {
        this.cardError = 'Payment system not ready. Please try again.';
        return;
      }
      if (this.cardError) {
        this.paymentStatus = 'failed';
        return;
      }

      const { error: tokenError } = await this.stripe.createToken(this.cardElement);
      if (tokenError) {
        this.cardError = tokenError.message;
        this.paymentStatus = 'failed';
        return;
      }

      this.paymentStatus = 'processing';
      this.cardError = null;

      try {
        this.clientSecret = await createPaymentIntent(this.depositAmount);

        if (!this.clientSecret) {
          throw new Error('Failed to create payment intent');
        }

        // Check if this is a mock payment (for testing)
        if (this.clientSecret.startsWith('pi_mock_')) {
          if (!this.cardError) {
            this.paymentStatus = 'success';
            setTimeout(async () => {
              this.saveDepositToHistory();
              this.depositPlaced = true;
              this.depositNumber = this.generateDepositNumber();
              this.depositDate = new Date().toLocaleDateString() + ' ' + this.$t('deposit.receipt.dateAt') + ' ' + new Date().toLocaleTimeString();
              await this.updateReservationStatus('confirmed');
              this.scrollToReceipt();
            }, 2000);
          } else {
            this.paymentStatus = 'failed';
          }
          return;
        }

        // Real Stripe payment
        const { error } = await this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.cardElement,
          },
        });

        if (error) {
          this.cardError = error.message;
          this.paymentStatus = 'failed';
        } else {
          this.paymentStatus = 'success';
          setTimeout(async () => {
            this.saveDepositToHistory();
            this.depositPlaced = true;
            this.depositNumber = this.generateDepositNumber();
            this.depositDate = new Date().toLocaleDateString() + ' ' + this.$t('deposit.receipt.dateAt') + ' ' + new Date().toLocaleTimeString();
            await this.updateReservationStatus('confirmed');
            this.scrollToReceipt();
          }, 2000);
        }
      } catch (error) {
        console.error('Payment error:', error);
        this.cardError = 'Payment failed. Please try again.';
        this.paymentStatus = 'failed';
      }
    },
    generateDepositNumber() {
      return 'DEP-' + Date.now().toString().slice(-8) + '-' + Math.random().toString(36).substr(2, 4).toUpperCase();
    },
    saveDepositToHistory() {
      // Store cart data for receipt display before clearing
      this.receiptCartTotal = this.cartTotal;
      this.receiptServiceFee = this.serviceFee;
      this.receiptTotalAmount = this.depositAmount; // Store the calculated total amount
      
      const depositData = {
        depositNumber: this.depositNumber,
        depositDate: this.depositDate,
        paymentMethod: this.paymentMethod,
        amount: this.depositAmount,
        type: 'deposit'
      };
      
      const existingDeposits = JSON.parse(localStorage.getItem('depositHistory') || '[]');
      existingDeposits.unshift(depositData);
      const recentDeposits = existingDeposits.slice(0, 10);
      localStorage.setItem('depositHistory', JSON.stringify(recentDeposits));
      
      // Clear cart after successful deposit
      localStorage.removeItem('cart');
      this.cart = [];
    },
    scrollToReceipt() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    },
    printReceipt() {
      window.print();
    },
    goToHome() {
      // Clear booking data from localStorage after successful booking
      const bookingKeys = [
        'bookingName',
        'bookingPhone',
        'bookingEmail',
        'bookingGuests',
        'bookingDate',
        'reservationId',
        'date',
        'duration',
        'selectedTable',
        'selectedTableName',
        'cart'
      ];
      
      bookingKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      
      clearBookingData();

      
      this.$router.push({ name: 'Home' });
    },
    getTranslatedPaymentMethod(method) {
      return this.$t(`deposit.payment.methods.${method}`);
    },
    async loadReservationId() {
      const reservationId = localStorage.getItem('reservationId');
      if (reservationId && reservationId.trim() !== '') {
        this.reservationId = reservationId;
        console.log('Reservation ID loaded:', reservationId);
      } else {
        console.log('No reservation ID found, redirecting to bookingduration');
        // Redirect to bookingduration if no reservation ID found
        this.$router.push('/bookingduration');
        return;
      }
    },
    async updateReservationStatus(status) {
      if (!this.reservationId) return;

      const db = getFirestore();
      const reservationRef = doc(db, 'reservations', this.reservationId);

      try {
        await updateDoc(reservationRef, {
          status: status,
          confirmedAt: serverTimestamp(),
          fullPayment: this.depositAmount,
          paymentMethod: this.paymentMethod,
          depositNumber: this.depositNumber,
          depositDate: this.depositDate,
        });
        console.log(`Reservation ${this.reservationId} status updated to ${status}`);
        
        // Send confirmation email if status is confirmed
        if (status === 'confirmed') {
          // Get reservation data for email
          const reservationData = {
            name: localStorage.getItem('bookingName') || 'Guest',
            email: localStorage.getItem('bookingEmail') || '',
            date: localStorage.getItem('bookingDate') || '',
            duration: localStorage.getItem('duration') || '',
            tableName: localStorage.getItem('selectedTableName') || 'TBD',
            paymentMethod: this.paymentMethod,
            total: this.depositAmount
          };
          
          // Send confirmation email
          await sendConfirmationEmail(reservationData);
        }
      } catch (error) {
        console.error('Error updating reservation status:', error);
      }
    }
  },
  watch: {
    paymentMethod(newMethod) {
      if (newMethod === 'card') {
        this.cardError = null; // Clear any previous errors
        this.paymentStatus = null; // Clear any previous status
        this.$nextTick(() => {
          this.setupStripeElements();
        });
      } else if (newMethod === 'cash') {
        this.cardError = null; // Clear card errors when switching to cash
        this.paymentStatus = null; // Clear payment status
      }
    }
  },
  beforeRouteLeave(to, from, next) {
  if (this.depositPlaced) {
    const bookingKeys = [
      'bookingName',
      'bookingPhone',
      'bookingEmail',
      'bookingGuests',
      'bookingDate',
      'reservationId',
      'date',
      'duration',
      'selectedTable',
      'selectedTableName',
      'cart'
    ];
    bookingKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('Booking data cleared on navigation after receipt');
  }
  next();
}

};
</script>

<style scoped>
@media print {
  /* Hide navbar and other page elements */
  nav, .navbar, header, footer {
    display: none !important;
  }
  
  /* Hide menu icons and header elements */
  .gear-icon, .hamburger-menu, .header-icons, .menu-icons {
    display: none !important;
  }
  
  /* Hide mobile/phone menus */
  .mobile-menu, .phone-menu, .responsive-menu, .dropdown-menu, .menu-dropdown {
    display: none !important;
  }
  
  /* Hide any menu-related elements */
  [class*="menu"], [class*="nav"], [class*="header"] {
    display: none !important;
  }
  
  /* Hide all body content except receipt */
  body * {
    visibility: hidden;
  }
  
  /* Show only the receipt */
  .receipt-print, .receipt-print * {
    visibility: visible;
  }
  
  .receipt-print {
    background: white !important;
    color: black !important;
  }
  
  .receipt-print * {
    color: black !important;
  }
  
  .receipt-print .text-\[\#F5BE32\] {
    color: #F5BE32 !important;
  }
}
</style>

